import { dateDeLaBulle as dateDUneBulle, type DateDUneBulle } from '@nextilt/commun';
import type { Bulle } from '../partage/api/types';
import { aujourdhui, jourEtHeureDUnInstant } from '../partage/datesEnMots';
import { avantLe, majuscule, proprietesEnMots } from '../partage/vocabulaire';
import { piecesJointes } from '../fiche';
import type { InfoDUneBulle } from './modes';

/*
 * Ce que disent les bulles quand une touche d'info est enfoncée (chantier modes-de-la-carte,
 * modification 02) : à la place du titre, une info (Réflexion) ou deux (Exécution). Chaque info
 * se dit avec les mots que l'app dit déjà (le contact de « Joint », la durée de la ligne des
 * réglages, le rendez-vous de Maintenant, l'échéance de la Liste) : rien ne se recalcule ici.
 */

type BulleLue = Pick<
  Bulle,
  'contactNom' | 'contactTel' | 'lieuTexte' | 'lien' | 'effortMin' | 'debut' | 'dateEcheance'
>;

/**
 * La date d'une bulle, par la règle de `commun/` (`dateDeLaBulle`, que l'Analyse lit au serveur) :
 * le rendez-vous d'abord, son jour là où est le navigateur, sinon l'échéance ; `null`, aucune. D
 * (Réflexion, Exécution) la dit en mots.
 */
export function dateDeLaBulle(bulle: Pick<Bulle, 'debut' | 'dateEcheance'>): DateDUneBulle | null {
  return dateDUneBulle(bulle, (instant) => aujourdhui(new Date(instant)));
}

/** Une info d'une bulle, en mots, ou `null` : elle ne l'a pas. */
export function infoEnMots(bulle: BulleLue, info: InfoDUneBulle): string | null {
  switch (info) {
    case 'contact':
      return bulle.contactNom ?? bulle.contactTel;
    case 'contactEtNumero':
      return piecesJointes(bulle).find((p) => p.sorte === 'contact')?.nom ?? null;
    case 'lieu':
      return bulle.lieuTexte;
    case 'duree':
      return proprietesEnMots(bulle, ['duree'])[0] ?? null;
    case 'echeance':
      return bulle.dateEcheance ? majuscule(avantLe(bulle.dateEcheance)) : null;
    case 'date': {
      // Le rendez-vous d'abord, un instant ; sinon l'échéance, un jour.
      const date = dateDeLaBulle(bulle);
      if (!date) return null;
      return majuscule(date.sorte === 'rendez_vous' ? jourEtHeureDUnInstant(new Date(date.instant)) : avantLe(date.jour));
    }
  }
}

/**
 * Ce qu'une bulle montre à la place de son titre : ses infos posées, jointes par « · » ; `null`
 * quand elle n'en a aucune (elle garde alors son titre, pâli). Deux infos qui disent la même chose
 * (la date et l'échéance d'une bulle sans rendez-vous) ne se disent qu'une fois.
 */
export function infosAuLieuDuTitre(bulle: BulleLue, infos: readonly InfoDUneBulle[]): string | null {
  const mots = [...new Set(infos.map((i) => infoEnMots(bulle, i)).filter((m): m is string => Boolean(m)))];
  return mots.length > 0 ? mots.join(' · ') : null;
}

/** Sans majuscule ni accent : « Éric » se trouve en tapant « eric ». */
function aPlat(texte: string): string {
  return texte.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('fr-FR');
}

/**
 * La recherche de la carte (/) : une bulle est trouvée quand son titre, sa note, son lieu ou son
 * contact (nom, numéro) contient ce qu'on a tapé, sans tenir compte des majuscules ni des
 * accents. Rien de tapé : toutes sont trouvées.
 */
export function trouveeDans(
  bulle: Pick<Bulle, 'titre' | 'notes' | 'lieuTexte' | 'contactNom' | 'contactTel'>,
  cherche: string
): boolean {
  const q = aPlat(cherche.trim());
  if (!q) return true;
  return [bulle.titre, bulle.notes, bulle.lieuTexte, bulle.contactNom, bulle.contactTel].some(
    (champ) => champ != null && aPlat(champ).includes(q)
  );
}
